import { Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { TagOutlined } from "@ant-design/icons";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../../../utils/translations";
import {
  posModalStyles as modalStyles,
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";
import { CustomPriceRow } from "./CustomPriceRow";
import { useSetItemPrices } from "../../../../../hooks/useBills";
import { useMenuItems } from "../../../../../hooks/useMenu";
import { useViewport } from "../../../../../hooks/useViewport";
import type { BillOut, SetItemPriceLineIn } from "../../../../../api/bills";

interface CustomPriceModalProps {
  open: boolean;
  onClose: () => void;
  billId: number | null;
  bill: BillOut | undefined;
}

const roundToCent = (value: number): number => Math.round(value * 100) / 100;

/** Цената в момента = цената от менюто след вече приложения процент. */
const priceAfterPercent = (price: string, percent: string): number =>
  roundToCent((parseFloat(price) * (100 - parseFloat(percent))) / 100);

/** Приема и точка, и запетая; връща null при невалиден/празен текст. */
const parsePriceInput = (raw: string): number | null => {
  const normalized = raw.replace(",", ".").trim();
  if (normalized === "" || normalized === ".") return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Изчиства всичко освен цифри и ЕДИН десетичен разделител (макс. 2 знака). */
const sanitizePriceInput = (raw: string): string => {
  const cleaned = raw.replace(/[^\d.,]/g, "").replace(",", ".");
  const [whole, ...rest] = cleaned.split(".");
  if (rest.length === 0) return whole;
  return `${whole}.${rest.join("").slice(0, 2)}`;
};

/** Ключ на ред в чернова/състояние — продуктът и добавката имат отделни id-та. */
const productKey = (billElementItemId: number) => `item:${billElementItemId}`;
const additionKey = (additionId: number) => `addition:${additionId}`;

interface EditableRow {
  key: string;
  billElementItemId: number;
  additionId: number | null;
  name: string;
  quantity: number;
  menuPrice: number;
  currentPrice: number;
  locked: boolean;
}

/** Промяна на цени по сметка — БЕЗ предварително избран продукт: показва всички
 *  поръчани продукти и добавките им, всеки с молив за нова единична цена.
 *  Записът е ЕДНА заявка с всички пипнати редове. */
export const CustomPriceModal = ({
  open,
  onClose,
  billId,
  bill,
}: CustomPriceModalProps) => {
  const { t, i18n } = useTranslation();
  const message = useOrderingMessage();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const allItems = useMenuItems();
  const setItemPricesMutation = useSetItemPrices();

  // Ключ на ред → текстът в полето му. Ред без запис не се редактира.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Модалът се отваря ЧИСТ — нито един ред не е в режим на редакция.
  useEffect(() => {
    if (open) setDrafts({});
  }, [open]);

  const getItemName = useCallback(
    (itemNames: Record<string, string>) =>
      itemNames[i18n.language] ||
      itemNames.bg ||
      Object.values(itemNames)[0] ||
      t("common.product"),
    [i18n.language, t],
  );

  // Заключената цена на ДОБАВКА не е снимана на реда (само продуктовите редове
  // носят `locked_price`), затова я четем от менюто по продаваемия ѝ id.
  const isAdditionLocked = useCallback(
    (additionItemId: number) =>
      allItems.find((menuItem) => menuItem.id === additionItemId)
        ?.lockedPrice ?? false,
    [allItems],
  );

  // Партидите (bill elements) остават групи, за да е ясно от коя поръчка е редът.
  const batches = useMemo(() => {
    if (!bill) return [];
    return bill.elements.map((element) => ({
      id: element.id,
      orderTime: element.order_time,
      rows: element.items.flatMap((item): EditableRow[] => [
        {
          key: productKey(item.id),
          billElementItemId: item.id,
          additionId: null,
          name: getItemName(item.item_names),
          quantity: parseFloat(item.quantity),
          menuPrice: parseFloat(item.price),
          currentPrice: priceAfterPercent(item.price, item.percent),
          locked: item.locked_price,
        },
        ...item.additions.map((addition) => ({
          key: additionKey(addition.id),
          billElementItemId: item.id,
          additionId: addition.id,
          name: getItemName(addition.item_names),
          quantity: parseFloat(addition.quantity),
          menuPrice: parseFloat(addition.price),
          currentPrice: priceAfterPercent(addition.price, addition.percent),
          locked: isAdditionLocked(addition.addition_item_id),
        })),
      ]),
    }));
  }, [bill, getItemName, isAdditionLocked]);

  const allRows = useMemo(
    () => batches.flatMap((batch) => batch.rows),
    [batches],
  );

  /** Ред с невалидна стойност в полето — празно или 0. Горен таван НЯМА. */
  const isDraftInvalid = useCallback((draft: string): boolean => {
    const parsed = parsePriceInput(draft);
    return parsed === null || parsed <= 0;
  }, []);

  const invalidRows = allRows.filter(
    (row) => drafts[row.key] !== undefined && isDraftInvalid(drafts[row.key]),
  );

  // Редове с валидна стойност, различна от текущата цена — само те се пращат.
  const changedLines: SetItemPriceLineIn[] = allRows
    .filter((row) => {
      const draft = drafts[row.key];
      if (draft === undefined || isDraftInvalid(draft)) return false;
      return roundToCent(parsePriceInput(draft) as number) !== row.currentPrice;
    })
    .map((row) => ({
      billElementItemId: row.billElementItemId,
      additionId: row.additionId,
      price: roundToCent(parsePriceInput(drafts[row.key]) as number),
    }));

  const canSave =
    !!billId && changedLines.length > 0 && invalidRows.length === 0;

  const handleSave = useCallback(async () => {
    if (!billId || changedLines.length === 0) return;
    setSaving(true);
    try {
      await setItemPricesMutation.mutateAsync({
        billId,
        data: { billId, lines: changedLines },
      });
      message.success(
        t("customPrice.applySuccess", { count: changedLines.length }),
      );
      onClose();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, t("common.error")));
    } finally {
      setSaving(false);
    }
  }, [billId, changedLines, setItemPricesMutation, message, t, onClose]);

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (canSave && !saving) handleSave();
      } else if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация.
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isDesktop, canSave, saving, handleSave, onClose]);

  const setDraft = (key: string, next: string | undefined) =>
    setDrafts((prev) => {
      const draft = { ...prev };
      if (next === undefined) delete draft[key];
      else draft[key] = next;
      return draft;
    });

  const formatBatchTime = (iso: string): string =>
    new Date(iso).toLocaleTimeString(i18n.language, {
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={isMobile ? undefined : isTablet ? 500 : 560}
      styles={modalStyles}
      className="max-w-[calc(100vw-32px)]"
    >
      <div className="bg-pos-panel">
        <ModalHeader
          icon={<TagOutlined />}
          title={t("customPrice.title")}
          subtitle={t("customPrice.subtitle")}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          className={`flex max-h-[52vh] flex-col gap-3 overflow-y-auto ${
            isNarrow ? "p-3" : "px-[22px] py-4"
          }`}
        >
          {allRows.length === 0 && (
            <p className="m-0 py-6 text-center text-sm text-pos-muted">
              {t("customPrice.emptyBill")}
            </p>
          )}

          {batches.map((batch) => (
            <div key={batch.id} className="flex flex-col gap-1.5">
              <span className="pl-1 font-pos-mono text-[11px] font-semibold uppercase tracking-[.09em] text-pos-muted">
                {formatBatchTime(batch.orderTime)}
              </span>
              <div className="overflow-hidden rounded-[14px] border border-pos-line3 bg-pos-card">
                {batch.rows.map((row) => (
                  <CustomPriceRow
                    key={row.key}
                    name={row.name}
                    quantity={row.quantity}
                    menuPrice={row.menuPrice}
                    currentPrice={row.currentPrice}
                    locked={row.locked}
                    isAddition={row.additionId !== null}
                    draft={drafts[row.key]}
                    invalid={
                      drafts[row.key] !== undefined &&
                      isDraftInvalid(drafts[row.key])
                    }
                    restoreLabel={t("customPrice.restore")}
                    onStartEdit={() =>
                      setDraft(row.key, row.currentPrice.toFixed(2))
                    }
                    onDraftChange={(next) =>
                      setDraft(row.key, sanitizePriceInput(next))
                    }
                    onCancelEdit={() => setDraft(row.key, undefined)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {(invalidRows.length > 0 || changedLines.length > 0) && (
          <div
            className={`border-t border-pos-line2 bg-pos-panel text-center ${
              isNarrow ? "px-3 py-2" : "px-[22px] py-2.5"
            }`}
          >
            {invalidRows.length > 0 ? (
              <span className="text-[12.5px] font-semibold text-pos-danger-ink">
                {t("customPrice.invalidRows", { count: invalidRows.length })}
              </span>
            ) : (
              <span className="text-[12.5px] font-semibold text-pos-soft2">
                {t("customPrice.changedCount", { count: changedLines.length })}
              </span>
            )}
          </div>
        )}

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.back")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={t("common.save")}
            onClick={handleSave}
            disabled={!canSave}
            loading={saving}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
};
